// (2026-04-24, Jason Cox) Inventory page. Consumes the existing Smart
// backend md/v1/listBaseRfid endpoint (no backend changes). Requires
// the user to pick a SmartBox before any data is fetched; aggregates
// the returned per-tag rows by SKU for an on-hand view.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedApi } from '../hooks/useUnifiedApi';
import { useSmartBoxList } from '../hooks/useSmartBoxList';
import type { BaseRfidRow } from '../services/unifiedApiService';
import { ArchiveBoxIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// One tag's row after normalization + aggregation keys.
interface InventoryLine {
  sku: string;
  name: string;
  tagCount: number;
  totalQty: number;
}

const FETCH_PAGE_SIZE = 500;
const UI_PAGE_SIZE = 50;
const ON_HAND_STATUS = 3; // md_rfid.status: 3 = On Hand

function aggregate(rows: BaseRfidRow[]): InventoryLine[] {
  const byKey = new Map<string, InventoryLine>();
  for (const r of rows) {
    const sku = r.sku || '(no sku)';
    const name = r.Name || '';
    const qty = typeof r.packageQty === 'number' ? r.packageQty : Number(r.packageQty) || 0;
    const key = sku;
    const existing = byKey.get(key);
    if (existing) {
      existing.tagCount += 1;
      existing.totalQty += qty;
      // prefer a non-empty name if we see one
      if (!existing.name && name) existing.name = name;
    } else {
      byKey.set(key, { sku, name, tagCount: 1, totalQty: qty });
    }
  }
  const out = Array.from(byKey.values());
  out.sort((a, b) => b.totalQty - a.totalQty || a.sku.localeCompare(b.sku));
  return out;
}

const Inventory: React.FC = () => {
  const { user } = useAuth();
  const api = useUnifiedApi();

  const { smartboxes, loading: smartboxesLoading } = useSmartBoxList();
  const [smartboxId, setSmartboxId] = useState<number | ''>('');

  // (2026-04-28) SKU/item search. The user types into `skuSearch`;
  // `appliedSku` lags behind by 400ms so we don't fire a fetch on every
  // keystroke. The fetch effect re-runs when appliedSku changes.
  const [skuSearch, setSkuSearch] = useState('');
  const [appliedSku, setAppliedSku] = useState('');

  const [rows, setRows] = useState<BaseRfidRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<number | null>(null);
  const [uiPage, setUiPage] = useState(1);

  const load = useCallback(async () => {
    if (!api || !api.getBaseRfidList) return;
    if (smartboxId === '') return;  // must pick a smartbox first
    setLoading(true);
    setError(null);
    try {
      const res = await api.getBaseRfidList({
        smartboxId,
        status: ON_HAND_STATUS,
        page: 1,
        pageSize: FETCH_PAGE_SIZE,
        sku: appliedSku.trim() || undefined,
      });
      setRows(res.data);
      setTotalRows(res.total);
      setUiPage(1);
      setLastFetched(Date.now());
    } catch (e: any) {
      setError(e?.message || 'Failed to load inventory');
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [api, smartboxId, appliedSku]);

  useEffect(() => {
    if (smartboxId === '') {
      setRows([]);
      setTotalRows(0);
      setUiPage(1);
      return;
    }
    load();
  }, [smartboxId, appliedSku, load]);

  // Debounce the search input -> appliedSku.
  useEffect(() => {
    const t = setTimeout(() => setAppliedSku(skuSearch), 400);
    return () => clearTimeout(t);
  }, [skuSearch]);

  // Reset the search when the smartbox changes -- fresh context.
  useEffect(() => {
    setSkuSearch('');
    setAppliedSku('');
  }, [smartboxId]);

  const lines = useMemo(() => aggregate(rows), [rows]);
  const totalUnits = useMemo(() => lines.reduce((acc, l) => acc + l.totalQty, 0), [lines]);
  const totalTags = useMemo(() => lines.reduce((acc, l) => acc + l.tagCount, 0), [lines]);
  const distinctSkus = lines.length;
  const truncated = totalRows > rows.length;

  const pageSlice = useMemo(() => {
    const start = (uiPage - 1) * UI_PAGE_SIZE;
    return lines.slice(start, start + UI_PAGE_SIZE);
  }, [lines, uiPage]);
  const totalUiPages = Math.max(1, Math.ceil(lines.length / UI_PAGE_SIZE));

  const selectedSmartbox = useMemo(
    () => smartboxes.find(s => s.id === smartboxId),
    [smartboxes, smartboxId],
  );

  if (user?.system !== 'smart') {
    return (
      <Layout>
        <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-secondary-600">Inventory is available for Smart users only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-secondary-900 sm:text-3xl sm:truncate flex items-center">
                <ArchiveBoxIcon className="h-8 w-8 mr-2 text-primary-600" />
                Inventory
              </h2>
              <p className="mt-1 text-sm text-secondary-600">
                On-hand inventory for a SmartBox, aggregated by SKU from the
                tags currently detected in that area.
              </p>
            </div>
            <div className="mt-3 md:mt-0 flex items-center space-x-2">
              {lastFetched && smartboxId !== '' && (
                <span className="text-xs text-secondary-500">
                  Last refreshed: {new Date(lastFetched).toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={load}
                disabled={loading || smartboxId === ''}
                className="btn-secondary flex items-center disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filter card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-secondary-700">SmartBox</label>
                  <select
                    value={smartboxId}
                    onChange={(e) => setSmartboxId(e.target.value ? parseInt(e.target.value, 10) : '')}
                    className="input mt-1 w-full"
                    disabled={smartboxesLoading}
                  >
                    <option value="">
                      {smartboxesLoading
                        ? 'Loading SmartBoxes...'
                        : `Select a SmartBox${smartboxes.length ? ` (${smartboxes.length})` : ''}`}
                    </option>
                    {smartboxes.map((sb) => (
                      <option key={sb.id} value={sb.id}>
                        {sb.code}{sb.name && sb.name !== sb.code ? ` -- ${sb.name}` : ''}
                        {sb.wName ? ` @ ${sb.wName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700">Search SKU</label>
                  <input
                    type="text"
                    value={skuSearch}
                    onChange={(e) => setSkuSearch(e.target.value)}
                    placeholder="e.g. WIDGET-001"
                    className="input mt-1 w-full"
                    disabled={smartboxId === ''}
                  />
                </div>
                <div className="flex items-end">
                  <div className="text-xs text-secondary-600 w-full">
                    {selectedSmartbox ? (
                      <div>
                        <span className="text-secondary-500">Selected:</span>{' '}
                        <span className="font-medium text-secondary-900">{selectedSmartbox.code}</span>
                        {selectedSmartbox.wName ? (
                          <span className="text-secondary-500"> @ {selectedSmartbox.wName}</span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="text-secondary-500">
                        Pick a SmartBox above to load its on-hand inventory.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary strip (only meaningful after selection) */}
          {smartboxId !== '' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="card p-4">
                <div className="text-xs text-secondary-500">Distinct items (SKUs)</div>
                <div className="text-2xl font-bold text-secondary-900">{distinctSkus.toLocaleString()}</div>
              </div>
              <div className="card p-4">
                <div className="text-xs text-secondary-500">On-hand tags</div>
                <div className="text-2xl font-bold text-secondary-900">{totalTags.toLocaleString()}</div>
                {truncated && (
                  <div className="mt-1 text-xs text-orange-700">
                    Showing first {rows.length.toLocaleString()} of {totalRows.toLocaleString()} tags.
                  </div>
                )}
              </div>
              <div className="card p-4">
                <div className="text-xs text-secondary-500">Total units</div>
                <div className="text-2xl font-bold text-secondary-900">{totalUnits.toLocaleString()}</div>
                <div className="mt-1 text-xs text-secondary-500">Sum of packageQty across on-hand tags.</div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md p-3 bg-red-50 text-red-800 text-sm mb-4">{error}</div>
          )}

          {/* Table */}
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 text-sm">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">SKU</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Item</th>
                  <th className="px-3 py-2 text-right font-medium text-secondary-700">Tags</th>
                  <th className="px-3 py-2 text-right font-medium text-secondary-700">Total qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {smartboxId === '' && (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-secondary-500">
                      Select a SmartBox to view its on-hand inventory.
                    </td>
                  </tr>
                )}
                {smartboxId !== '' && loading && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-secondary-500">Loading...</td></tr>
                )}
                {smartboxId !== '' && !loading && pageSlice.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-secondary-500">No on-hand tags found for this SmartBox.</td></tr>
                )}
                {smartboxId !== '' && !loading && pageSlice.map((line) => (
                  <tr key={line.sku} className="hover:bg-secondary-50">
                    <td className="px-3 py-2 font-mono text-xs text-secondary-900">{line.sku}</td>
                    <td className="px-3 py-2 text-secondary-700">{line.name || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{line.tagCount.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">{line.totalQty.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {smartboxId !== '' && totalUiPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="text-secondary-600">
                Page {uiPage} of {totalUiPages} ({lines.length} SKUs)
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setUiPage(p => Math.max(1, p - 1))}
                  disabled={uiPage <= 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setUiPage(p => Math.min(totalUiPages, p + 1))}
                  disabled={uiPage >= totalUiPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Inventory;
